import { useEffect, useRef, useCallback } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { useAppSession } from '../hooks/useAppSession';
import { useBillingInfo } from '../hooks/useBillingInfo';
import { useJoinCandidates } from '../hooks/useTeams';
import { onboardingApi, teamsApi, teamJoinRequestsApi } from '../lib/api';
import { OnboardingWizard, INVITED_STORAGE_KEY } from '../components/onboarding/OnboardingWizard';
import { Logo } from '../components/ui/Logo';
import type { OnboardingCompleteRequest } from '@overlayqa/shared';
import type { UrlType } from '../components/onboarding/StepProject';
import type { InviteSeatInfo } from '../components/onboarding/StepInviteTeam';
import { log } from '@/lib/logger';
import { capture } from '../lib/analytics';
import { writePendingExtensionOpen } from '@/lib/pendingExtensionOpen';
import { runOnboardingFinish, canUseTeamReview, type ReviewPreflightSummary } from '../lib/onboardingFinish';
import { clientReviewsApi } from '../lib/api/clientReviews';
import { readFreeToolSite } from '../lib/freeToolSite';

const OVERLAY_HOST_ID = 'overlayqa-overlay-host';

// Hard client-side ceiling on the pre-flight probe (the server's own fetch
// budget is 6s). Finish must never hang on a slow probe.
const PREFLIGHT_CLIENT_TIMEOUT_MS = 8_000;

/**
 * Ask the server whether the review proxy can render this URL (TODOS #73).
 * FAIL-OPEN: any error or timeout returns undefined, which proceeds to team
 * review exactly as before the probe existed.
 */
async function probeReviewabilityFailOpen(
  token: string | undefined,
  url: string,
): Promise<ReviewPreflightSummary | undefined> {
  try {
    return await Promise.race([
      clientReviewsApi.preflight(token, url),
      new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), PREFLIGHT_CLIENT_TIMEOUT_MS)),
    ]);
  } catch (err) {
    log.warn('[OnboardingPage] preflight probe failed; failing open', {
      error: err instanceof Error ? err.message : String(err),
    });
    return undefined;
  }
}

const ONBOARDING_STARTED_KEY_PREFIX = 'oqa_onboarding_started:';

function hasStartedOnboarding(userId: string): boolean {
  try {
    return localStorage.getItem(`${ONBOARDING_STARTED_KEY_PREFIX}${userId}`) === '1';
  } catch {
    return false;
  }
}

function markOnboardingStarted(userId: string): void {
  try {
    localStorage.setItem(`${ONBOARDING_STARTED_KEY_PREFIX}${userId}`, '1');
  } catch {
    // Storage unavailable: the next mount reports again, as before.
  }
}

export function OnboardingPage() {
  const { getToken } = useAuth();
  // The project step asks this once the URL settles; the answer is the weekly scan's own probe.
  const handleScanPreflight = useCallback(
    async (url: string) => onboardingApi.scanPreflight((await getToken()) ?? undefined, url),
    [getToken],
  );
  const { data: session, isLoading, isError: sessionFailed } = useAppSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const finishInFlightRef = useRef(false);

  // Step 5 (invite teammates) reads the team's seats so it can hide itself on
  // a plan with nothing to fill and name the real count in its helper. The
  // wizard copes without it (step shown, seat sentence omitted), so a slow or
  // failed billing read never blocks the wizard.
  const { data: billingInfo } = useBillingInfo();
  const seats: InviteSeatInfo | null = billingInfo
    ? {
        planName: billingInfo.planName,
        maxSeats: billingInfo.maxSeats,
        isTrial: billingInfo.betaExpiresAt !== null,
        invitableSeats: Math.max(0, billingInfo.maxSeats - billingInfo.currentSeats),
      }
    : null;

  // Invited slim wizard (Fix 5): entered via /onboarding?invited=1 from
  // InvitePage, or via the sessionStorage signal so a mid-wizard refresh that
  // drops the query param stays in invited mode.
  let invitedTeamId: string | null = null;
  try {
    invitedTeamId = sessionStorage.getItem(INVITED_STORAGE_KEY);
  } catch {
    // ignore, storage access can throw in private mode
  }
  const invited = searchParams.get('invited') === '1' || Boolean(invitedTeamId);
  const invitedTeamName = invited
    ? session?.teams?.find((t) => t.id === invitedTeamId)?.name ?? session?.team?.name ?? null
    : null;

  // Step 5 JOIN state (spec 2026-09-10): the owner-matched teams for this
  // signup's work-email domain. Read once the session exists and only for the
  // owner wizard (an invited signup is already joining a team); the server
  // answers empty while its flag is off, so the wizard renders the invite form.
  // isLoading is true only for the first fetch while it is enabled, so an
  // invited or already-onboarded person (the query disabled) never sees the
  // wizard's loading state, and an error settles it back to the invite form.
  const { data: joinCandidates, isLoading: joinCandidatesPending } = useJoinCandidates({
    enabled: !invited && !isLoading && Boolean(session) && !session?.user?.onboardingCompleted,
  });
  // Step 5 is unknowable until the session answers: the candidates read is
  // gated on it (just above) and TanStack reads a disabled query as NOT
  // loading, so a wizard restored onto step 5 by a reload drew the invite form
  // for the whole session round-trip and swapped it for the join offer once
  // both answers landed (frame 08 of the 2026-09-10 run of record: the form
  // sat on screen 786 ms before the offer). That wait is pending too. A failed
  // session read settles to the invite form (fail open), and an invited signup
  // never waits, since its step 5 is not the join state.
  const sessionUnsettled = !invited && !session && !sessionFailed;

  log.info('[OnboardingPage] render', {
    isLoading,
    invited,
    joinCandidates: joinCandidates?.candidates.length ?? null,
    onboardingCompleted: session?.user?.onboardingCompleted,
  });

  // Track onboarding entry exactly once per fresh user. Skip if the user
  // is already complete or still loading. The ref alone made it once per
  // MOUNT: a refresh, or a browser Back on step 1 that goes through Google and
  // /auth/callback and lands here again (production session 01a16f12,
  // 2026-10-24), fired a second start. A per-user marker in localStorage keeps
  // it to one; storage that throws falls back to once per mount.
  const viewedRef = useRef(false);
  const sessionUserId = session?.user?.id;
  useEffect(() => {
    if (viewedRef.current) return;
    if (isLoading) return;
    if (session?.user?.onboardingCompleted) return;
    viewedRef.current = true;
    if (sessionUserId && hasStartedOnboarding(sessionUserId)) {
      log.info('[OnboardingPage] onboarding already started for this user; not re-reporting', { userId: sessionUserId });
      return;
    }
    // prefilled_site: the wizard opened with a free scan tool's audited site (lib/freeToolSite.ts). Fires once per
    // user, on the first visit, which is the fresh wizard that took the site.
    capture('dashboard_onboarding_started', { prefilled_site: readFreeToolSite() !== null });
    if (sessionUserId) markOnboardingStarted(sessionUserId);
  }, [isLoading, session?.user?.onboardingCompleted, sessionUserId]);

  // The dev reset (DevResetButton) wipes the server account, then full-reloads
  // here. The extension's chrome.storage must be wiped too, but DevResetButton
  // can't do it reliably — its content script may be a post-rebuild zombie.
  // This page just finished a fresh load, so its content script is current:
  // fire the clear from here, where it reliably reaches the background worker.
  // removeItem keeps it idempotent under StrictMode's double-invoked effects.
  useEffect(() => {
    let pending = false;
    try {
      pending = sessionStorage.getItem('oqa_extension_reset_pending') === '1';
      if (pending) sessionStorage.removeItem('oqa_extension_reset_pending');
    } catch {
      // ignore — storage access can throw in private mode
    }
    if (pending) {
      log.info('[OnboardingPage] dev-reset pending — clearing extension storage');
      window.postMessage({ type: 'OVERLAYQA_DEV_RESET' }, window.location.origin);
    }
  }, []);

  // OnboardingWizard.handleFinish calls onSave before invitations and review
  // creation. That save marks the server session complete; a refetch must not
  // mount the dashboard while this wizard is still preparing its destination.
  if (!finishInFlightRef.current && !isLoading && session?.user?.onboardingCompleted) {
    log.info('[OnboardingPage] onboarding already completed, redirecting to /dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSave(data: OnboardingCompleteRequest): Promise<{ projectId?: string; projectName?: string; projectSlug?: string; teamId?: string }> {
    finishInFlightRef.current = true;
    try {
      log.info('[OnboardingPage] handleSave called', { data });
      const token = await getToken();
      if (!token) {
        log.error('[OnboardingPage] handleSave — no token!');
        throw new Error('Not authenticated');
      }
      const result = await onboardingApi.complete(token, data);
      log.info('[OnboardingPage] onboarding save response', { result });
      capture('dashboard_onboarding_completed', {
        has_team_name: Boolean((data as { teamName?: string }).teamName),
        role: data.role,
      });
      return {
        projectId: result?.project?.id,
        projectName: result?.project?.name,
        projectSlug: result?.project?.slug,
        // The team the save resolved (renamed to the step-1 company name); step
        // 5's invites go here, never to whatever the session held before.
        teamId: result?.team?.id,
      };
    } catch (error) {
      finishInFlightRef.current = false;
      // 409 "Onboarding is already complete." means the server finished this
      // account earlier (another tab, or a wizard re-mounted on a stale
      // session). Refresh the session so the redirect above takes the user to
      // /dashboard instead of leaving them on a Finish that can never succeed.
      if ((error as { status?: number }).status === 409) {
        log.warn('[OnboardingPage] onboarding already complete on the server; refreshing the session');
        void queryClient.invalidateQueries({ queryKey: ['app-session'] });
      }
      throw error;
    }
  }

  /**
   * Step 5's send, run by the wizard after the save. One POST per address,
   * sequential (four at most), and every refusal is handled here: a seat-cap
   * 403, an already-a-member 409 and an email-provider 500 are the server's
   * own answers, declared expected so they log at warn rather than as a fake
   * $exception. Nothing throws; the wizard routes to the review either way,
   * and the counts are what the caller logs.
   */
  async function handleInvite(teamId: string, emails: string[]): Promise<{ sent: number; failed: number }> {
    const token = (await getToken()) ?? undefined;
    let sent = 0;
    let failed = 0;
    for (const email of emails) {
      try {
        await teamsApi.invite(token, teamId, { email, role: 'member' }, { expectedErrorStatuses: [403, 409, 500] });
        sent += 1;
        capture('invitation_sent', { role: 'member', source: 'onboarding' });
      } catch (err) {
        failed += 1;
        const e = err as { status?: number; code?: string; message?: string };
        log.warn('[OnboardingPage] onboarding invite refused', { email, status: e.status, code: e.code, message: e.message });
        capture('invitation_send_failed', { source: 'onboarding', status: e.status ?? 0, code: e.code ?? 'unknown' });
      }
    }
    log.info('[OnboardingPage] onboarding invites done', { teamId, sent, failed });
    return { sent, failed };
  }

  /**
   * Step 5's "Request to join" (spec 2026-09-10). One POST; the server's three
   * refusals (feature off 403, not-a-candidate 403, already-a-member 409) are
   * declared expected in the API client so they log at warn. The refusal is
   * re-thrown with its sentence so the wizard shows it inline and stays on
   * the offer; success is reported here, the offer and the decline in the step.
   */
  async function handleRequestJoin(teamId: string): Promise<void> {
    const token = (await getToken()) ?? undefined;
    try {
      const request = await teamJoinRequestsApi.create(token, teamId);
      log.info('[OnboardingPage] join request sent', { teamId, requestId: request.id, status: request.status });
      capture('onboarding_join_requested', { team_id: teamId, candidates: joinCandidates?.candidates.length ?? 0 });
    } catch (err) {
      const e = err as { status?: number; code?: string; message?: string };
      log.warn('[OnboardingPage] join request refused', { teamId, status: e.status, code: e.code, message: e.message });
      capture('onboarding_join_request_failed', { status: e.status ?? 0, code: e.code ?? 'unknown' });
      throw err;
    }
  }


  async function handleFinish(urlInfo: { url: string; urlType: UrlType; projectId?: string; projectName?: string; projectSlug?: string; teamId?: string }) {
    const hasExtension = !!document.getElementById(OVERLAY_HOST_ID);

    // Attach teamId so team review / the extension can build a full Project object.
    const teamId = session?.activeTeamId;
    const fullInfo = { ...urlInfo, teamId };

    // A reachable URL goes straight to in-app team review (the lowest-friction
    // first-capture path); a local/private one falls back to the extension.
    // Computed synchronously so the routing split rides the existing event.
    const useReview = canUseTeamReview(fullInfo);

    const token = await getToken();

    // Pre-flight probe (TODOS #73): a login-walled or unreachable site routes
    // to the extension path instead of a broken review canvas. Fail-open —
    // probe errors and timeouts proceed to team review (pre-probe behavior).
    const preflight = useReview ? await probeReviewabilityFailOpen(token ?? undefined, urlInfo.url) : undefined;
    const goingToReview = useReview && preflight?.reviewable !== false;

    // Capture the selected destination before leaving the page. The refreshed
    // session stays available for the dashboard fallback without redirecting
    // this in-flight wizard away from its pending review.
    capture('onboarding_url_selected', {
      url_type: urlInfo.urlType,
      url_value: urlInfo.url,
      extension: hasExtension,
      url_reachable: useReview,
      intended_destination: goingToReview ? 'team_review' : 'extension',
      preflight_verdict: preflight?.reason ?? (useReview ? 'skipped' : 'not-probed'),
    });

    await queryClient.invalidateQueries({ queryKey: ['app-session'] });

    log.info('[OnboardingPage] handleFinish', { url: urlInfo.url, hasExtension, projectId: urlInfo.projectId, useReview, preflight });

    let reviewFailure: string | undefined;
    const destination = await runOnboardingFinish(fullInfo, {
      hasExtension,
      reportReviewFailure: (error) => {
        const failure = error as { status?: number; code?: string; message?: string } | null;
        reviewFailure = failure?.message || 'The review service could not be reached.';
        log.error('[OnboardingPage] team review could not open', { error });
        capture('onboarding_review_failed', {
          project_id: fullInfo.projectId,
          status: failure?.status ?? 0,
          code: failure?.code ?? 'unknown',
          reason: error instanceof Error ? error.name : 'unknown',
        });

      },
      createLiveReview: (input) => clientReviewsApi.createLiveReview(token ?? undefined, input),
      // Cross-origin navigation: the review page (api.overlayqa.com) is built to
      // load top-level (X-Frame-Options: DENY), so we navigate rather than embed.
      navigateToReview: (url) => { window.location.assign(url); },
      openSiteViaExtension: (info) =>
        window.postMessage({ type: 'OVERLAYQA_OPEN_SITE', payload: info }, window.location.origin),
      // localStorage stash survives the Chrome Web Store install detour even if
      // this dashboard tab gets closed (see lib/pendingExtensionOpen).
      writePending: (info) => writePendingExtensionOpen({ ...info, userId: session?.user.id }),
      // A site that refused our server goes to the extension path; say why there.
      navigateToDashboard: () => navigate('/dashboard', {
        state: reviewFailure ? { onboardingReviewFailure: reviewFailure }
          : preflight?.reason === 'access-denied' || preflight?.reason === 'auth-required'
            ? { onboardingSiteRefused: true } : undefined,
      }),
    }, { preflight });

    log.info('[OnboardingPage] handleFinish destination', { destination });
  }

  return (
    <main className="min-h-screen bg-gray-50">
      {/* On a phone a tall step (step 4 once a site is in the field) ran under the logo (2026-11-01 phone pass): the
          logo sits on the card's 16px inset there and the column keeps 48px top and bottom, so the card starts below
          it. Desktop keeps its 24px logo inset, and symmetric padding leaves a card that fits centred where it was. */}
      <div className="absolute top-3 left-4 sm:top-6 sm:left-6">
        <Logo showText size="md" />
      </div>
      <div className="flex items-center justify-center min-h-screen px-4 py-12">
        <OnboardingWizard
          onSave={handleSave}
          onFinish={handleFinish}
          onScanPreflight={handleScanPreflight}
          onInvite={handleInvite}
          seats={seats}
          joinCandidates={joinCandidates ?? null}
          joinCandidatesPending={joinCandidatesPending || sessionUnsettled}
          onRequestJoin={handleRequestJoin}
          invited={invited}
          invitedTeamName={invitedTeamName}
          onInvitedFinish={async () => {
            // Refresh BEFORE navigating: useAppSession caches the session for
            // 5 minutes, and Layout's onboarding guard reads that cache, so a
            // bare navigate bounced every invited teammate back to /onboarding
            // into the owner wizard (incident 2026-10-23). The owner finish
            // does the same refresh in handleFinish above.
            await queryClient.invalidateQueries({ queryKey: ['app-session'] });
            navigate('/dashboard');
          }}
        />
      </div>
    </main>
  );
}
